// Public ROI View - Customer-facing page that does NOT require authentication
// Accessed via /roi/share?regions={JSON}&view=customer
// All data is encoded in the URL - no DB or API calls needed

import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useROIStore } from './store'
import { getProduct } from './data'
import { CustomerRegionSummary } from './components/CustomerRegionSummary'
import { FlowVisualization } from './components/FlowVisualization'
import { ROIAnalysis } from './components/ROIAnalysis'
import { BenchmarkReference } from './components/BenchmarkReference'
import type { Region } from './types'

export function PublicROIView() {
  const { regions, selectedFlowRegionId, setSelectedFlowRegion, loadROIState, setCustomerView } = useROIStore()
  const [searchParams] = useSearchParams()
  const [loadError, setLoadError] = useState<string | null>(null)
  const [isLoaded, setIsLoaded] = useState(false)

  const regionsParam = searchParams.get('regions')

  // Load regions from URL params on mount
  useEffect(() => {
    if (!regionsParam) {
      setLoadError('No ROI data found in the link. Please request a new link from your contact.')
      return
    }

    try {
      const regionsData = JSON.parse(regionsParam) as Array<{
        n?: string; c: string; i: string; p: string; t: string
        r: number; v: number; val: number; s: string
      }>

      const loadedRegions: Region[] = regionsData.map((rd, idx) => {
        const stackProfiles = rd.s ? rd.s.split('|').filter(Boolean) : []
        const stack = stackProfiles
          .map(profileId => {
            const product = getProduct(profileId)
            return product ? {
              profile: product.profile,
              description: product.description,
              passRate: product.passRate,
              incrementalLift: product.incrementalLift,
            } : null
          })
          .filter((item): item is NonNullable<typeof item> => item !== null)

        return {
          id: idx + 1,
          name: rd.n || '',
          country: rd.c || '',
          industry: rd.i || '',
          provider: rd.p || '',
          product: rd.t || '',
          passRate: rd.r || 0,
          volume: rd.v || 0,
          valuePerCustomer: rd.val || 0,
          costPerCheck: 0,
          stack,
        }
      })

      if (loadedRegions.length > 0) {
        loadROIState({ regions: loadedRegions })
        setCustomerView(true)
        setIsLoaded(true)
      } else {
        setLoadError('No regions found in the link data.')
      }
    } catch (e) {
      console.error('Failed to parse ROI regions from URL:', e)
      setLoadError('Invalid link data. Please request a new link from your contact.')
    }
  }, [regionsParam])

  // Error state
  if (loadError) {
    return (
      <div className="min-h-screen bg-[var(--bg-primary)] flex items-center justify-center p-6">
        <div className="max-w-md text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-danger/10 flex items-center justify-center">
            <span className="text-2xl">⚠️</span>
          </div>
          <h1 className="text-xl font-semibold text-[var(--text-primary)] mb-2">Unable to Load ROI Analysis</h1>
          <p className="text-sm text-[var(--text-muted)]">{loadError}</p>
        </div>
      </div>
    )
  }

  // Loading state
  if (!isLoaded) {
    return (
      <div className="min-h-screen bg-[var(--bg-primary)] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  const getRegionLabel = (region: Region) => region.name || region.country || `Region ${region.id}`

  return (
    <div className="min-h-screen bg-[var(--bg-primary)]">
      {/* Clean public page container */}
      <div className="max-w-6xl mx-auto px-6 py-8 space-y-6">
        {/* Header */}
        <div className="bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-6">
          <div className="text-center">
            <h1 className="text-2xl font-semibold text-[var(--text-primary)]">ROI Analysis</h1>
            <p className="text-sm text-[var(--text-muted)] mt-1">
              The value FrankieOne delivers by improving pass rates
            </p>
          </div>
        </div>

        {/* Region tabs (only show if multiple regions) */}
        {regions.length > 1 && (
          <div className="flex gap-2">
            {regions.map((region) => (
              <button
                key={region.id}
                onClick={() => setSelectedFlowRegion(region.id)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  selectedFlowRegionId === region.id
                    ? 'bg-primary text-white'
                    : 'bg-[var(--bg-secondary)] border border-[var(--border-color)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:border-primary/50'
                }`}
              >
                {getRegionLabel(region)}
              </button>
            ))}
          </div>
        )}

        {/* Selected region summary — read-only in public view */}
        {/* CustomerRegionSummary, FlowVisualization, ROIAnalysis, and BenchmarkReference
            are all display-only components with no edit controls. The region tabs above
            are navigation only (switching which region is displayed). No store mutations
            for region data are exposed in this view. */}
        {regions
          .filter((r) => r.id === selectedFlowRegionId || (!selectedFlowRegionId && r.id === regions[0]?.id))
          .map((region) => (
            <CustomerRegionSummary key={region.id} region={region} />
          ))}

        {/* Flow + Analysis — all read-only display components */}
        <div className="space-y-4">
          <FlowVisualization />
          <ROIAnalysis />
          <BenchmarkReference />
        </div>

        {/* Footer */}
        <div className="text-center py-4">
          <p className="text-xs text-[var(--text-muted)]">
            Powered by FrankieOne · This analysis is based on industry benchmarks and actual verification data
          </p>
        </div>
      </div>
    </div>
  )
}
